import { SHORT_PERIOD_LABELS } from "./periods";

/*
Dashboard click-to-filter (RMAS-129).

Every card, chart bar and team-velocity row in the three dashboard responses
carries a `filters` dict of ready-made tickets/ query params. Clicking one
narrows the case list to exactly the tickets that card counts, so the list
`count` matches the number on the card - as long as the list is sent the same
scope params the dashboard was.

A selection is stored as a stable id (it lives in the `card` URL param, so it
survives a refresh) rather than as the `filters` themselves, and is re-resolved
against the latest responses on every render. The dates inside `filters`
follow the period, so switching the period pill while a card is active swaps
in the new range instead of leaving the list on the old one.

Ids:
  performance:<kpi key>        data.filters[<kpi key>]
  bucket:<bucket start>:<kind> data.chart.buckets[i].filters[new|closed]
  snapshot:<key>               data.filters[<key>]  (KPI row and SLA cards)
  velocity:<handler_id>        data.handlers[i].filters

A bucket is matched on its start date, not its index - index 0 of a 7d chart
and index 0 of a 90d chart are different ranges.
*/

const PERFORMANCE_LABELS = {
  new_tickets: "New tickets",
  closed: "Closed",
  open_backlog: "Open backlog",
  // Same filter as "closed" - the average is taken over those cases.
  avg_close_time_days: "Closed",
};

const SNAPSHOT_LABELS = {
  open: "Open cases",
  pending_approval: "Pending approval",
  completed: "Completed",
  doa_claims: "DOA claims",
  no_response_48h: "No response in 48h",
  blocked: "On Hold tickets",
  unresolved_past_sla: "Unresolved past SLA",
};

const pick = function (filters, key) {
  const value = filters && filters[key];
  return value && typeof value === "object" ? value : null;
};

const withSuffix = function (label, suffix) {
  return suffix ? label + " (" + suffix + ")" : label;
};

/*
Returns { filters, label } for a card id, or null when the latest responses
don't carry that card (not loaded yet, errored, or gone after a period change).
*/
export const resolveCardFilter = function (id, sources, period) {
  if (!id) return null;
  const parts = id.split(":");
  const kind = parts[0];
  const performance = sources.performance && sources.performance.data;
  const snapshot = sources.snapshot && sources.snapshot.data;
  const velocity = sources.velocity && sources.velocity.data;

  if (kind === "performance") {
    const filters = pick(performance && performance.filters, parts[1]);
    if (!filters || !PERFORMANCE_LABELS[parts[1]]) return null;
    return {
      filters,
      label: withSuffix(
        PERFORMANCE_LABELS[parts[1]],
        performance.period_label
      ),
    };
  }
  if (kind === "snapshot") {
    const filters = pick(snapshot && snapshot.filters, parts[1]);
    if (!filters || !SNAPSHOT_LABELS[parts[1]]) return null;
    const label =
      parts[1] === "completed"
        ? withSuffix(SNAPSHOT_LABELS.completed, SHORT_PERIOD_LABELS[period])
        : SNAPSHOT_LABELS[parts[1]];
    return { filters, label };
  }
  if (kind === "bucket") {
    const buckets =
      (performance && performance.chart && performance.chart.buckets) || [];
    const bucket = buckets.filter(function (b) {
      return String(b.start) === parts[1];
    })[0];
    const filters = pick(bucket && bucket.filters, parts[2]);
    if (!filters) return null;
    return {
      filters,
      label: (parts[2] === "new" ? "New" : "Closed") + " · " + bucket.label,
    };
  }
  if (kind === "velocity") {
    const handlers = (velocity && velocity.handlers) || [];
    const handler = handlers.filter(function (h) {
      return String(h.handler_id) === parts[1];
    })[0];
    if (!handler || !handler.filters || typeof handler.filters !== "object") {
      return null;
    }
    return {
      filters: handler.filters,
      label: "Completed by " + (handler.handler || "handler"),
    };
  }
  return null;
};

export const bucketFilterId = function (bucket, barKind) {
  return "bucket:" + bucket.start + ":" + barKind;
};
